import { useTranslations } from "adapters/i18n";
import { useMemo } from "react";
import type { EnrichedAnimalCategory, OperationType } from "shared/types";
import { RecordType } from "../utils/movements.utils";

type UseMovementsFormOptions = {
  categories: EnrichedAnimalCategory[];
  operationTypes: OperationType[];
  selectedRecordType: RecordType | null;
};

export function useMovementsFormOptions({
  categories,
  operationTypes,
  selectedRecordType,
}: UseMovementsFormOptions) {
  const { t } = useTranslations();

  const categoryOptions = useMemo(
    () =>
      categories
        .map((category) => ({
          label: category.name,
          value: category.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [categories]
  );

  const recordTypeOptions = useMemo(
    () =>
      [
        {
          value: RecordType.Entry,
          label: t("movements:entry"),
        },
        {
          value: RecordType.Exit,
          label: t("movements:exit"),
        },
      ].sort((a, b) => a.label.localeCompare(b.label)),
    [t]
  );

  const operationTypeLabelsByType = {
    entry: [t("movements:transfer"), t("movements:buy")],
    exit: [t("movements:transfer"), t("movements:sale")],
  };

  const entryOperationTypesOptions = useMemo(
    () =>
      operationTypes
        .filter((item) => operationTypeLabelsByType.entry.includes(item.name))
        .map((operationType) => ({
          label: operationType.name,
          value: operationType.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [operationTypeLabelsByType.entry, operationTypes]
  );

  const exitOperationTypesOptions = useMemo(
    () =>
      operationTypes
        .filter((item) => operationTypeLabelsByType.exit.includes(item.name))
        .map((operationType) => ({
          label: operationType.name,
          value: operationType.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [operationTypeLabelsByType.exit, operationTypes]
  );

  const operationTypeOptions = useMemo(() => {
    if (selectedRecordType == null) {
      return [];
    }
    return selectedRecordType === RecordType.Entry
      ? entryOperationTypesOptions
      : exitOperationTypesOptions;
  }, [
    entryOperationTypesOptions,
    exitOperationTypesOptions,
    selectedRecordType,
  ]);

  return {
    categoryOptions,
    operationTypeOptions,
    recordTypeOptions,
  };
}
